/**
 * The approval ladder every submission climbs.
 *
 *   To Do -> Created -> Checked -> Reviewed -> Approved
 *             teacher   levelHead  registrar  principal
 *
 * with Discarded and Resubmit as the branches back to the teacher. The same
 * ladder governs grades, conduct and remarks; PACE competency ratings ride on
 * the grade row for their section-subject and term, because the summary letter
 * and the ratings beneath it are one act of assessment and must not be able to
 * diverge.
 */

/** A submission is with the teacher, and only then, in these three states. */
var TEACHER_EDITABLE = ['To Do', 'Discarded', 'Resubmit'];

/**
 * Whether the submission is still with the teacher rather than the approvers.
 * Status only - it says nothing about who is looking at it.
 */
export function isTeacherEditable(status) {
  return TEACHER_EDITABLE.indexOf(status) !== -1;
}

/** True once the principal has signed off. */
export function isApproved(status) {
  return status === 'Approved';
}

/**
 * Whether this user may change the marks at this point on the ladder.
 *
 * Editing rights move up the ladder with the submission: the teacher owns it
 * until they submit, the level head while it is Created, and the registrar or
 * principal from Checked onward. Admin can always edit.
 *
 * Lifted verbatim from the check GradesPV has always made inline, so that the
 * PACE grid freezes on exactly the same rule. If these two ever disagree, a
 * competency rating could be edited out from under an approved grade.
 *
 * @param {string} status            the sectionSubjectDetail status
 * @param {object} user              { userType, isSubjectTeacher }
 */
export function canEditMarks(status, user) {
  var u = user || {};
  if (u.userType === 'admin') return true;

  if (isTeacherEditable(status)) return !!u.isSubjectTeacher;
  if (status === 'Created') return u.userType === 'levelHead';

  // Checked, Reviewed and Approved. Note that this leaves an approved
  // submission editable by the registrar and principal - existing behaviour,
  // preserved deliberately rather than tightened here.
  return u.userType === 'principal' || u.userType === 'registrar';
}

/**
 * The subjects on a card whose marks have not been approved yet.
 *
 * The card has always printed regardless of where its grades sit on the
 * ladder, at every level. That is not tightened here - a registrar printing an
 * early copy is a legitimate thing to do - but it is worth saying out loud on
 * screen, so nobody hands a parent a card built on marks the principal has not
 * signed.
 *
 * @param {array}  sectionSubjects  with `details` included
 * @param {number} term             the period being printed
 * @returns {array} subject names, in the order given
 */
export function unapprovedSubjects(sectionSubjects, term) {
  return (sectionSubjects || []).reduce(function (names, sectionSubject) {
    var details = sectionSubject.details || [];
    var detail = details.find(function (d) {
      return d.type === 'grade' && d.quarter === term && d.active !== false;
    });
    if (!detail || !isApproved(detail.status)) {
      var subject = sectionSubject.subject;
      names.push((subject && subject.name) || 'Unnamed subject');
    }
    return names;
  }, []);
}

/**
 * What to show the user. Only 'Resubmit' reads badly as a status - it is a
 * state, not an instruction - so it is the one the UI renames.
 */
export function statusLabel(status) {
  if (status === 'Resubmit') return 'For Resubmission';
  return status || 'To Do';
}

export { TEACHER_EDITABLE };
